import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { BackendProvider, loggingOut, useSession } from './backend';
import type { Backend } from './backend/types';
import { ConfirmProvider, Logo, ToastProvider } from './components/ui';
import { NotFound, PublicSite } from './pages/PublicSite';
import type { PublicClient } from './backend/types';

// Guests only download what a wedding website needs; the builder loads when it's opened
const Landing = lazy(() => import('./pages/Landing').then((m) => ({ default: m.Landing })));
const AuthPage = lazy(() => import('./pages/Auth').then((m) => ({ default: m.AuthPage })));
const AuthAction = lazy(() => import('./pages/Auth').then((m) => ({ default: m.AuthAction })));
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Builder = lazy(() => import('./pages/Builder').then((m) => ({ default: m.Builder })));

/** A guest opening a wedding website: just that page, without sign-in or the builder. */
export function PublicApp({ client }: { client: PublicClient }) {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/:slug" element={<PublicSite client={client} standalone />} />
      </Routes>
    </BrowserRouter>
  );
}

function Loading({ text = 'Loading your account…' }: { text?: string }) {
  return <div className="view v-loading"><div><Logo /><p>{text}</p></div></div>;
}

/** Pages for people who are logged in. Others are sent to log in, then brought back. */
function RequireUser({ children }: { children: ReactNode }) {
  const { user, ready } = useSession();
  const loc = useLocation();
  if (!ready) return <Loading />;
  if (!user && loggingOut.current) return <Navigate to="/" replace />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

/** Landing and sign-in pages send people who are already logged in to their websites. */
function GuestOnly({ children }: { children: ReactNode }) {
  const { user, ready } = useSession();
  const loc = useLocation();
  if (!ready) return <Loading />;
  loggingOut.current = false;
  if (user) {
    const next = new URLSearchParams(loc.search).get('next');
    return <Navigate to={next && next.startsWith('/') && !next.startsWith('//') ? next : '/dashboard'} replace state={loc.state} />;
  }
  return <>{children}</>;
}

export function App({ backend }: { backend: Backend }) {
  return (
    <BackendProvider backend={backend}>
      <ToastProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <Suspense fallback={<Loading text="Loading…" />}>
            <Routes>
              <Route path="/" element={<GuestOnly><Landing /></GuestOnly>} />
              <Route path="/login" element={<GuestOnly><AuthPage mode="login" /></GuestOnly>} />
              <Route path="/signup" element={<GuestOnly><AuthPage mode="signup" /></GuestOnly>} />
              <Route path="/forgot" element={<AuthPage mode="forgot" />} />
              <Route path="/auth/action" element={<AuthAction />} />
              <Route path="/dashboard" element={<RequireUser><Dashboard /></RequireUser>} />
              <Route path="/edit/:siteId" element={<RequireUser><Builder /></RequireUser>} />
              <Route path="/:slug" element={<PublicSite client={backend} />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </ConfirmProvider>
      </ToastProvider>
    </BackendProvider>
  );
}
